import { useForm, usePage, Link } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { ConfigurationFields } from "@/components/sources/ConfigurationFields"
import { PayloadMappingEditor, TEMPLATE_VARIABLES } from "@/components/sources/PayloadMappingEditor"
import { formatHourShort, useTimezone } from "@/lib/date"
import type { Source, SourceType, SourceCategory, PayloadMapping, SharedPageProps, IntegrationType } from "@/types"
import type { FormEvent } from "react"
import { useState } from "react"

const SOURCE_TYPE_LABELS: Record<string, string> = {
  webhook: "Webhook",
  rss: "RSS",
  podcast_rss: "Podcast RSS",
  mimeo: "Mimeo",
  youtube: "YouTube",
  discord: "Discord",
}

const CATEGORY_LABELS: Record<string, string> = {
  owned: "Owned",
  watched: "Watched",
}

const INTEGRATION_SOURCE_TYPES: Record<string, IntegrationType> = {
  mimeo: "mimeo",
  discord: "discord",
}

const INTEGRATION_NAMES: Record<string, string> = {
  mimeo: "Mimeo",
  discord: "Discord",
}

const POLL_UNITS = [
  { value: "hour", label: "Hour(s)", minutes: 60 },
  { value: "day", label: "Day(s)", minutes: 1440 },
  { value: "week", label: "Week(s)", minutes: 10080 },
  { value: "month", label: "Month(s)", minutes: 43200 },
  { value: "year", label: "Year(s)", minutes: 525600 },
]

function minutesToPollInterval(totalMinutes: number): { value: number; unit: string } {
  for (const u of [...POLL_UNITS].reverse()) {
    if (totalMinutes >= u.minutes && totalMinutes % u.minutes === 0) {
      return { value: totalMinutes / u.minutes, unit: u.value }
    }
  }
  return { value: Math.max(1, Math.round(totalMinutes / 60)), unit: "hour" }
}

function pollIntervalToMinutes(value: number, unit: string): number {
  const u = POLL_UNITS.find((p) => p.value === unit)
  return value * (u?.minutes ?? 60)
}

function getDefaultMappings(sourceType: SourceType): PayloadMapping[] {
  const vars = TEMPLATE_VARIABLES[sourceType] || []
  return vars.slice(0, 4).map((v) => ({ key: v, value: `{{${v}}}` }))
}

interface SourceFormProps {
  source?: Source
  sourceTypes: string[]
  categories: string[]
  isEditing?: boolean
}

export function SourceForm({ source, sourceTypes, categories, isEditing = false }: SourceFormProps) {
  const { props } = usePage<SharedPageProps>()
  const integrationConnections = props.integration_connections || {}
  const githubDestination = props.github_destination
  const timezone = useTimezone()

  function isIntegrationConnected(sourceType: string): boolean {
    const integrationType = INTEGRATION_SOURCE_TYPES[sourceType]
    if (!integrationType) return true // non-integration types are always available
    return integrationConnections[integrationType]?.status === "connected"
  }

  const initialPoll = minutesToPollInterval(source?.polling_interval ?? 60)
  const [pollValue, setPollValue] = useState(initialPoll.value)
  const [pollUnit, setPollUnit] = useState(initialPoll.unit)

  const { data, setData, post, patch, processing, errors } = useForm({
    source_type: source?.source_type || (sourceTypes[0] as SourceType) || "webhook",
    name: source?.name || "",
    category: source?.category || (categories[0] as SourceCategory) || "owned",
    enabled: source?.enabled ?? true,
    daily_digest: source?.daily_digest ?? false,
    deliver_to_webhook: source?.deliver_to_webhook ?? true,
    deliver_to_github: source?.deliver_to_github ?? false,
    github_path_template: source?.github_path_template || "",
    configuration: source?.configuration || {},
    payload_mappings: source?.payload_mappings || getDefaultMappings((sourceTypes[0] as SourceType) || "webhook"),
    polling_interval: source?.polling_interval ?? 60,
    poll_time_of_day: source?.poll_time_of_day ?? null as number | null,
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (isEditing && source) {
      patch(`/admin/sources/${source.id}`)
    } else {
      post("/admin/sources")
    }
  }

  function handleSourceTypeChange(value: string) {
    const newType = value as SourceType
    setData((prev) => ({
      ...prev,
      source_type: newType,
      configuration: {},
      payload_mappings: isEditing ? prev.payload_mappings : getDefaultMappings(newType),
    }))
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="source_type">Source Type</Label>
            <Select
              value={data.source_type}
              onValueChange={handleSourceTypeChange}
              disabled={isEditing}
            >
              <SelectTrigger id="source_type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {sourceTypes.map((type) => (
                  <SelectItem
                    key={type}
                    value={type}
                    disabled={!isIntegrationConnected(type)}
                  >
                    {SOURCE_TYPE_LABELS[type] || type}
                    {!isIntegrationConnected(type) && " (not connected)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.source_type && (
              <p className="text-destructive text-sm">{errors.source_type}</p>
            )}
            {INTEGRATION_SOURCE_TYPES[data.source_type] && !isIntegrationConnected(data.source_type) && (
              <p className="text-muted-foreground text-sm">
                Connect {INTEGRATION_NAMES[data.source_type]} in Integrations to use this source type.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              placeholder="My Feed Source"
              required
            />
            {errors.name && (
              <p className="text-destructive text-sm">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select
              value={data.category}
              onValueChange={(value) => setData("category", value as SourceCategory)}
            >
              <SelectTrigger id="category">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {CATEGORY_LABELS[cat] || cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.category && (
              <p className="text-destructive text-sm">{errors.category}</p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="enabled"
              checked={data.enabled}
              onCheckedChange={(checked) => setData("enabled", checked)}
            />
            <Label htmlFor="enabled">Enabled</Label>
          </div>

          <div className="space-y-2">
            <Label>Configuration</Label>
            <ConfigurationFields
              sourceType={data.source_type}
              configuration={data.configuration}
              onChange={(config) => setData("configuration", config)}
            />
            {errors.configuration && (
              <p className="text-destructive text-sm">{errors.configuration}</p>
            )}
          </div>

          {data.source_type !== "webhook" && (
            <>
              <div className="space-y-2">
                <Label>Poll every</Label>
                <div className="flex gap-2">
                  <Input
                    id="polling_interval"
                    type="number"
                    min={1}
                    className="w-24"
                    value={pollValue}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10) || 1
                      setPollValue(v)
                      setData("polling_interval", pollIntervalToMinutes(v, pollUnit))
                    }}
                  />
                  <Select
                    value={pollUnit}
                    onValueChange={(unit) => {
                      setPollUnit(unit)
                      setData("polling_interval", pollIntervalToMinutes(pollValue, unit))
                    }}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {POLL_UNITS.map((u) => (
                        <SelectItem key={u.value} value={u.value}>
                          {u.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {errors.polling_interval && (
                  <p className="text-destructive text-sm">{errors.polling_interval}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Time of day to poll</Label>
                <Select
                  value={data.poll_time_of_day !== null ? String(data.poll_time_of_day) : "any"}
                  onValueChange={(value) =>
                    setData("poll_time_of_day", value === "any" ? null : parseInt(value, 10))
                  }
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Any time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any time</SelectItem>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {formatHourShort(i)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground text-xs">
                  Restrict polling to a specific hour ({timezone}).
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lookback_days">Lookback days</Label>
                <Input
                  id="lookback_days"
                  type="number"
                  min={0}
                  value={(data.configuration.lookback_days as number) ?? 30}
                  onChange={(e) =>
                    setData("configuration", {
                      ...data.configuration,
                      lookback_days: parseInt(e.target.value, 10) || 0,
                    })
                  }
                />
                <p className="text-muted-foreground text-xs">
                  Only capture items from the last N days. Set to 0 for no limit.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <Switch
                    id="daily_digest"
                    checked={data.daily_digest}
                    onCheckedChange={(checked) => setData("daily_digest", checked)}
                  />
                  <Label htmlFor="daily_digest">Daily digest</Label>
                </div>
                <p className="text-muted-foreground text-xs">
                  Combine captures from each day into a single delivery.
                </p>
              </div>
            </>
          )}

          <div className="space-y-3">
            <Label>Delivery</Label>
            {(() => {
              const baseError = (errors as { base?: string | string[] }).base
              if (!baseError) return null
              return (
                <p className="text-destructive text-sm">
                  {Array.isArray(baseError) ? baseError.join(", ") : baseError}
                </p>
              )
            })()}
            <div className="flex items-center gap-3">
              <Switch
                id="deliver_to_webhook"
                checked={data.deliver_to_webhook}
                onCheckedChange={(checked) => setData("deliver_to_webhook", checked)}
              />
              <Label htmlFor="deliver_to_webhook">Send to webhook</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="deliver_to_github"
                checked={data.deliver_to_github}
                onCheckedChange={(checked) => setData("deliver_to_github", checked)}
              />
              <Label htmlFor="deliver_to_github">Send to GitHub repo</Label>
            </div>
            {data.deliver_to_github && (
              <div className="space-y-2">
                <Label htmlFor="github_path_template">GitHub path</Label>
                <Input
                  id="github_path_template"
                  value={data.github_path_template}
                  onChange={(e) => setData("github_path_template", e.target.value)}
                  placeholder="folder-a/folder-b/{date}-file-name"
                />
                <p className="text-muted-foreground text-xs">
                  <code>{"{date}"}</code> becomes today&apos;s date as YYYY-MM-DD. Files are always saved as{" "}
                  <code>.md</code> (the extension is added automatically).
                </p>
                {githubDestination?.connected && githubDestination.repo ? (
                  <p className="text-muted-foreground text-xs">
                    Repository: <span className="font-medium">{githubDestination.repo}</span>
                  </p>
                ) : (
                  <p className="text-muted-foreground text-xs">
                    Connect a GitHub repository in{" "}
                    <Link href="/admin/settings" className="text-primary underline underline-offset-2">
                      Settings
                    </Link>{" "}
                    before captures can be committed.
                  </p>
                )}
                {errors.github_path_template && (
                  <p className="text-destructive text-sm">{errors.github_path_template}</p>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Payload Mappings</Label>
            <PayloadMappingEditor
              mappings={data.payload_mappings}
              onChange={(mappings) => setData("payload_mappings", mappings)}
              sourceType={data.source_type}
            />
            {errors.payload_mappings && (
              <p className="text-destructive text-sm">{errors.payload_mappings}</p>
            )}
          </div>

          <div className="flex gap-3">
            <Button type="submit" disabled={processing}>
              {processing ? "Saving..." : isEditing ? "Update Source" : "Create Source"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
